"use client";

/**
 * 바이어 푸터 — 4열 화이트 디자인
 *
 * 데스크톱: 4-column grid
 * 모바일: Accordion 기반 1-column stack
 */
import Link from "next/link";

import { OPERATOR } from "@/shared/constants/operator";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Separator } from "@/components/ui/separator";

const SHOPPING_LINKS = [
  { href: "/faq/user", label: "이용안내" },
  { href: "/apply", label: "입점문의" },
  { href: "/flash-sale", label: "깜짝 할인" },
  { href: "/shopping", label: "전체 상품" },
] as const;

const LEGAL_LINKS = [
  { href: "/terms", label: "이용약관" },
  { href: "/privacy", label: "개인정보처리방침" },
  { href: "/business-info", label: "사업자정보확인" },
] as const;

/** 데스크톱 전용 열 */
function DesktopColumn({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="hidden md:block">
      <h3 className="mb-4 text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

export function MainFooter() {
  return (
    <footer className="relative z-10 w-full border-t bg-background text-muted-foreground">
      <div className="mx-auto max-w-screen-xl px-4 py-12">
        {/* Desktop: 4-column grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
          {/* Col 1: Brand — always visible */}
          <div>
            <img src="/logo.png" alt="DANGIL" className="mb-4 w-20 h-auto" />
            <p className="text-sm">감도 높은 도매 큐레이션</p>
            <div className="mt-3 flex gap-3">
              <Link
                href="https://www.instagram.com/dangil_wholesale/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="transition-colors hover:text-foreground"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
              </Link>
            </div>
          </div>

          {/* Col 2: Shopping Guide — desktop */}
          <DesktopColumn title="쇼핑 가이드">
            <ul className="space-y-2">
              {SHOPPING_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </DesktopColumn>

          {/* Col 3: Customer Support — desktop */}
          <DesktopColumn title="고객지원">
            <p className="text-sm font-semibold text-foreground">채널톡 문의</p>
            <p className="mt-2 text-sm">
              운영시간: 평일 10:00 - 18:00
              <br />
              (점심 12:00 - 13:00)
            </p>
            <p className="mt-1 text-sm">
              <a href={OPERATOR.phoneHref} className="hover:text-foreground">
                {OPERATOR.phone}
              </a>
            </p>
            <p className="mt-1 text-sm">{OPERATOR.email}</p>
          </DesktopColumn>

          {/* Col 4: Legal — desktop */}
          <DesktopColumn title="법적 고지">
            <ul className="space-y-2">
              {LEGAL_LINKS.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </DesktopColumn>
        </div>

        {/* Mobile: Accordion */}
        <div className="mt-8 md:hidden">
          <Accordion type="multiple">
            <AccordionItem value="shopping">
              <AccordionTrigger className="text-sm font-semibold text-foreground hover:no-underline">
                쇼핑 가이드
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-1">
                  {SHOPPING_LINKS.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="block py-2 text-sm transition-colors hover:text-foreground active:text-foreground"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="support">
              <AccordionTrigger className="text-sm font-semibold text-foreground hover:no-underline">
                고객지원
              </AccordionTrigger>
              <AccordionContent>
                <p className="text-sm font-semibold text-foreground">채널톡 문의</p>
                <p className="mt-2 text-sm">
                  운영시간: 평일 10:00 - 18:00
                  <br />
                  (점심 12:00 - 13:00)
                </p>
                <p className="mt-1 text-sm">
                  <a href={OPERATOR.phoneHref} className="hover:text-foreground">
                    {OPERATOR.phone}
                  </a>
                </p>
                <p className="mt-1 text-sm">{OPERATOR.email}</p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="legal">
              <AccordionTrigger className="text-sm font-semibold text-foreground hover:no-underline">
                법적 고지
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-1">
                  {LEGAL_LINKS.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="block py-2 text-sm transition-colors hover:text-foreground active:text-foreground"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <Separator className="my-8" />

        {/* Business Info */}
        <div className="text-xs text-muted-foreground/70">
          <p>
            상호명: {OPERATOR.name} | 대표: {OPERATOR.representative} | 사업자등록번호:{" "}
            {OPERATOR.registrationNumber}
          </p>
          <p className="mt-1">주소: {OPERATOR.address}</p>
          <p className="mt-1">
            전화번호:{" "}
            <a href={OPERATOR.phoneHref} className="hover:text-foreground">
              {OPERATOR.phone}
            </a>
            {" | "}이메일: {OPERATOR.email} | 호스팅: AWS Web Service
          </p>
          <p className="mt-4">&copy; 2024 dangil. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
